/**
 * VideoFrameDecoder - Core video decode engine for the draw-video feature.
 *
 * Loads an MP4 video from a URL, demuxes all samples upfront via mp4box.js,
 * and decodes frames via WebCodecs VideoDecoder.
 *
 * Two decode modes:
 * 1. Pump mode (startSequentialDecode): feeds ALL samples continuously in DTS
 *    order — the way WebCodecs is designed to work. Outputs arrive in display
 *    order and are served instantly via awaitFrame(). No resets, no flushes,
 *    no timeouts. Use this for sequential playback.
 * 2. Random-access mode (decodeFrame without pump): per-frame seek with
 *    keyframe rewind. Slower, but works for arbitrary access patterns.
 */

import { SampleInfo, DecoderState } from './types';

// mp4box 2.x types use TS 5.0 const type params that TS 4.9 can't parse.
// Use require + runtime resolution to avoid pulling in .d.ts files.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const MP4Box = require('mp4box');
const createFile: () => any = MP4Box.createFile;
const DataStreamClass: any = MP4Box.DataStream;

const RING_SIZE = 3;

export class VideoFrameDecoder {
  private device: GPUDevice;
  private samples: SampleInfo[] = [];
  private decoder: VideoDecoder | null = null;
  private decoderConfig: VideoDecoderConfig | null = null;
  private displayToDts: number[] = []; // maps display frame index → DTS index in samples[]
  private ringTextures: GPUTexture[] = [];
  private ringIndex: number = 0;
  private currentTexture: GPUTexture | null = null;
  private state: DecoderState = DecoderState.Idle;
  private _videoWidth: number = 0;
  private _videoHeight: number = 0;

  // Dispatch targets for decoder callbacks
  private _onFrame: ((frame: VideoFrame) => void) | null = null;
  private _onError: ((error: DOMException) => void) | null = null;

  // ── Pump state ──────────────────────────────────────────────────────────
  private pumpMode = false;
  private pumpAborted = false;
  // Decoded but not yet consumed VideoFrames, keyed by display index
  private decodedFrames: Map<number, VideoFrame> = new Map();
  // Resolve callbacks for frames the drain loop is waiting on
  private frameWaiters: Map<number, (frame: VideoFrame) => void> = new Map();
  // timestamp → display index reverse lookup (built once at pump start)
  private tsToDisplay: Map<number, number> = new Map();
  // Optional filter: only keep these display indices, close the rest
  private neededFrames: Set<number> | null = null;

  constructor(device: GPUDevice) {
    this.device = device;
  }

  // ── Load / demux ────────────────────────────────────────────────────────

  async load(videoUrl: string): Promise<void> {
    this.state = DecoderState.Loading;

    try {
      const response = await fetch(videoUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status} ${response.statusText}`);
      }
      const arrayBuffer = await response.arrayBuffer();

      const { config, samples, displayToDts, width, height } = await this.demux(arrayBuffer);

      this._videoWidth = width;
      this._videoHeight = height;
      this.decoderConfig = config;
      this.samples = samples;
      this.displayToDts = displayToDts;

      this.createRingBuffer(width, height);

      this.state = DecoderState.Ready;
    } catch (error) {
      this.state = DecoderState.Error;
      throw error;
    }
  }

  private demux(arrayBuffer: ArrayBuffer): Promise<{
    config: VideoDecoderConfig;
    samples: SampleInfo[];
    displayToDts: number[];
    width: number;
    height: number;
  }> {
    return new Promise((resolve, reject) => {
      const file = createFile();
      const rawSamples: any[] = [];
      let trackInfo: any = null;
      let codecDescription: Uint8Array | null = null;

      file.onReady = (info: any) => {
        const track = info.videoTracks[0];
        if (!track) {
          reject(new Error('No video track found in MP4'));
          return;
        }

        trackInfo = track;

        try {
          codecDescription = this.extractDescription(file, track);
        } catch (e) {
          codecDescription = null;
        }

        file.setExtractionOptions(track.id);
        file.start();
      };

      file.onSamples = (_trackId: number, _user: any, samples: any[]) => {
        rawSamples.push(...samples);
      };

      file.onError = (error: string) => {
        reject(new Error(`MP4Box error: ${error}`));
      };

      (arrayBuffer as any).fileStart = 0;
      file.appendBuffer(arrayBuffer);
      file.flush();

      if (!trackInfo) {
        reject(new Error('MP4 parsing failed: onReady was not called'));
        return;
      }

      // Keep samples in DTS order — VideoDecoder requires DTS-order chunks.
      const samples: SampleInfo[] = rawSamples.map((s, i) => ({
        data: new Uint8Array(s.data.slice(0)),
        isSync: s.is_sync,
        timestamp: (1e6 * s.cts) / s.timescale,
        duration: (1e6 * s.duration) / s.timescale,
        displayIndex: i,
      }));

      // Build display-order (CTS) → DTS index mapping.
      const ctsSorted = samples
        .map((s, dtsIdx) => ({ cts: s.timestamp, dtsIdx }))
        .sort((a, b) => a.cts - b.cts);
      const displayToDts: number[] = new Array(samples.length);
      for (let displayIdx = 0; displayIdx < ctsSorted.length; displayIdx++) {
        const dtsIdx = ctsSorted[displayIdx].dtsIdx;
        displayToDts[displayIdx] = dtsIdx;
        samples[dtsIdx].displayIndex = displayIdx;
      }

      const width = trackInfo.video.width;
      const height = trackInfo.video.height;

      const config: VideoDecoderConfig = {
        codec: trackInfo.codec,
        codedWidth: width,
        codedHeight: height,
        hardwareAcceleration: 'no-preference' as HardwarePreference,
      };

      if (codecDescription) {
        config.description = codecDescription;
      }

      resolve({ config, samples, displayToDts, width, height });
    });
  }

  private extractDescription(file: any, track: any): Uint8Array {
    const trak = file.getTrackById(track.id);
    for (const entry of trak.mdia.minf.stbl.stsd.entries) {
      const box = entry.avcC || entry.hvcC || entry.vpcC || entry.av1C;
      if (box) {
        const stream = new DataStreamClass(undefined, 0, DataStreamClass.BIG_ENDIAN);
        box.write(stream);
        return new Uint8Array(stream.buffer, 8);
      }
    }
    throw new Error('avcC, hvcC, vpcC, or av1C box not found');
  }

  // ── Decoder lifecycle ───────────────────────────────────────────────────

  private createDecoder(): void {
    this.decoder = new VideoDecoder({
      output: (frame: VideoFrame) => {
        if (this._onFrame) {
          this._onFrame(frame);
        } else {
          frame.close();
        }
      },
      error: (error: DOMException) => {
        if (this._onError) {
          this._onError(error);
        } else {
          console.error('VideoDecoder error:', error.message);
          this.state = DecoderState.Error;
        }
      },
    });
  }

  private feedSample(dtsIndex: number): void {
    const sample = this.samples[dtsIndex];
    this.decoder!.decode(
      new EncodedVideoChunk({
        type: sample.isSync ? 'key' : 'delta',
        timestamp: Math.round(sample.timestamp),
        duration: Math.round(sample.duration),
        data: sample.data,
      })
    );
  }

  // ── Sequential decode pump ──────────────────────────────────────────────

  /**
   * Start background decode pump for sequential playback.
   * Feeds ALL samples in DTS order — the way WebCodecs is designed.
   * Outputs arrive in display order and are buffered for instant retrieval
   * via decodeFrame().
   *
   * @param neededDisplayIndices Only keep these display frame indices.
   *   All other frames are closed immediately to save memory.
   */
  startSequentialDecode(neededDisplayIndices?: Set<number>): void {
    if (this.pumpMode) return;
    if (!this.decoderConfig) throw new Error('Call load() first');

    this.pumpMode = true;
    this.pumpAborted = false;
    this.neededFrames = neededDisplayIndices || null;

    // Build timestamp → display index reverse lookup
    this.tsToDisplay.clear();
    for (let d = 0; d < this.displayToDts.length; d++) {
      const dtsIdx = this.displayToDts[d];
      this.tsToDisplay.set(Math.round(this.samples[dtsIdx].timestamp), d);
    }

    // Create fresh decoder for pump
    if (this.decoder && this.decoder.state !== 'closed') {
      try { this.decoder.close(); } catch {}
    }
    this.createDecoder();
    this.decoder!.configure(this.decoderConfig);

    // Route all outputs through the pump handler
    this._onFrame = (frame: VideoFrame) => {
      const displayIdx = this.tsToDisplay.get(Math.round(frame.timestamp));
      if (displayIdx === undefined) {
        frame.close();
        return;
      }

      // Close frames we don't need
      if (this.neededFrames && !this.neededFrames.has(displayIdx)) {
        frame.close();
        return;
      }

      // If someone is already waiting for this frame, resolve immediately
      const waiter = this.frameWaiters.get(displayIdx);
      if (waiter) {
        this.frameWaiters.delete(displayIdx);
        waiter(frame);
      } else {
        // Buffer for later retrieval
        this.decodedFrames.set(displayIdx, frame);
      }
    };

    this._onError = (error: DOMException) => {
      console.error('[VideoFrameDecoder] Pump error:', error.message);
    };

    // Fire and forget — runs concurrently with the drain loop
    this.runPump();
  }

  private async runPump(): Promise<void> {
    // Max unconsumed frames to hold before pausing (memory backpressure).
    // Each VideoFrame is ~width*height*4 bytes; 10 frames ≈ 80 MB for 1080p.
    const MAX_BUFFERED = 10;

    for (let i = 0; i < this.samples.length; i++) {
      if (this.pumpAborted) break;
      if (!this.decoder || this.decoder.state === 'closed') break;

      this.feedSample(i);

      // Backpressure: pause feeding if too many unconsumed frames buffered.
      // This also yields to the event loop so output callbacks fire.
      while (this.decodedFrames.size > MAX_BUFFERED && !this.pumpAborted) {
        await new Promise<void>(r => setTimeout(r, 0));
      }
    }

    // Flush remaining frames from the reorder buffer
    if (!this.pumpAborted && this.decoder && this.decoder.state !== 'closed') {
      try { await this.decoder.flush(); } catch {}
    }
  }

  /**
   * Wait for a specific display-order frame from the pump.
   * Returns the decoded VideoFrame (caller must NOT close it — uploadFrame does).
   */
  private awaitFrame(displayIndex: number): Promise<VideoFrame> {
    // Already decoded and buffered?
    const ready = this.decodedFrames.get(displayIndex);
    if (ready) {
      this.decodedFrames.delete(displayIndex);
      return Promise.resolve(ready);
    }

    // Wait for the pump to produce it (with safety timeout)
    return new Promise<VideoFrame>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.frameWaiters.delete(displayIndex);
        reject(new Error(
          `awaitFrame(${displayIndex}) timed out after 5 s — frame never decoded`
        ));
      }, 5000);

      this.frameWaiters.set(displayIndex, (frame: VideoFrame) => {
        clearTimeout(timeout);
        resolve(frame);
      });
    });
  }

  // ── Public API ──────────────────────────────────────────────────────────

  /**
   * Decode a specific frame by display index and upload to the ring buffer.
   *
   * In pump mode: instant retrieval from the pre-decoded buffer.
   * Without pump: random-access seek (slower, resets decoder on backward DTS).
   */
  async decodeFrame(frameIndex: number): Promise<GPUTexture> {
    if (frameIndex < 0 || frameIndex >= this.displayToDts.length) {
      throw new Error(
        `Frame index ${frameIndex} out of range [0, ${this.displayToDts.length})`
      );
    }

    if (!this.decoderConfig) {
      throw new Error('Decoder not initialized. Call load() first.');
    }

    this.state = DecoderState.Decoding;

    try {
      if (this.pumpMode) {
        // Fast path: get frame from pump buffer
        const frame = await this.awaitFrame(frameIndex);
        const texture = this.uploadFrame(frame);
        this.state = DecoderState.Ready;
        return texture;
      }

      // Fallback: random-access decode (used for initial frame 0 before pump starts)
      return await this.decodeFrameRandomAccess(frameIndex);
    } catch (error) {
      this.state = DecoderState.Error;
      throw error;
    }
  }

  /**
   * Random-access decode: reset + seek from keyframe + flush.
   * Used for one-off frame requests (e.g. frame 0 before pump starts).
   */
  private async decodeFrameRandomAccess(frameIndex: number): Promise<GPUTexture> {
    const targetDtsIndex = this.displayToDts[frameIndex];
    const targetTimestamp = this.samples[targetDtsIndex].timestamp;

    // Create/reset decoder
    if (!this.decoder || this.decoder.state === 'closed') {
      this.createDecoder();
    } else {
      this.decoder.reset();
    }
    this.decoder!.configure(this.decoderConfig!);

    // Find nearest preceding keyframe
    let keyframeDtsIndex = targetDtsIndex;
    while (keyframeDtsIndex > 0 && !this.samples[keyframeDtsIndex].isSync) {
      keyframeDtsIndex--;
    }

    // Collect output
    let resolveTarget!: (frame: VideoFrame) => void;
    let rejectTarget!: (error: any) => void;
    const targetPromise = new Promise<VideoFrame>((resolve, reject) => {
      resolveTarget = resolve;
      rejectTarget = reject;
    });

    let resolved = false;
    this._onFrame = (frame: VideoFrame) => {
      if (resolved) { frame.close(); return; }
      if (Math.abs(frame.timestamp - targetTimestamp) < 1) {
        resolved = true;
        resolveTarget(frame);
      } else {
        frame.close();
      }
    };
    this._onError = (error: DOMException) => {
      if (!resolved) { resolved = true; rejectTarget(error); }
    };

    // Feed from keyframe through target
    for (let i = keyframeDtsIndex; i <= targetDtsIndex; i++) {
      this.feedSample(i);
    }

    // Flush to guarantee output (clears reorder buffer)
    try { await this.decoder!.flush(); } catch {}

    const frame = await targetPromise;
    const texture = this.uploadFrame(frame);

    this._onFrame = null;
    this._onError = null;
    this.state = DecoderState.Ready;
    return texture;
  }

  /**
   * Upload a decoded VideoFrame to the next ring buffer texture.
   * Closes the frame immediately after the GPU copy.
   */
  private uploadFrame(frame: VideoFrame): GPUTexture {
    const texture = this.ringTextures[this.ringIndex];

    this.device.queue.copyExternalImageToTexture(
      { source: frame },
      { texture },
      [frame.codedWidth, frame.codedHeight]
    );

    frame.close();

    this.currentTexture = texture;
    this.ringIndex = (this.ringIndex + 1) % RING_SIZE;

    return texture;
  }

  private createRingBuffer(width: number, height: number): void {
    for (let i = 0; i < RING_SIZE; i++) {
      this.ringTextures.push(
        this.device.createTexture({
          size: [width, height, 1],
          format: 'rgba8unorm' as GPUTextureFormat,
          usage:
            GPUTextureUsage.TEXTURE_BINDING |
            GPUTextureUsage.COPY_DST |
            GPUTextureUsage.RENDER_ATTACHMENT,
        })
      );
    }
  }

  getRingTextures(): GPUTexture[] {
    return this.ringTextures;
  }

  getLastDecodedRingIndex(): number {
    return (this.ringIndex - 1 + RING_SIZE) % RING_SIZE;
  }

  getCurrentTexture(): GPUTexture | null {
    return this.currentTexture;
  }

  get frameCount(): number {
    return this.displayToDts.length;
  }

  get videoWidth(): number {
    return this._videoWidth;
  }

  get videoHeight(): number {
    return this._videoHeight;
  }

  get currentState(): DecoderState {
    return this.state;
  }

  destroy(): void {
    this.pumpAborted = true;
    this.pumpMode = false;

    // Close all buffered VideoFrames
    this.decodedFrames.forEach(f => f.close());
    this.decodedFrames.clear();
    this.frameWaiters.clear();
    this.tsToDisplay.clear();
    this.neededFrames = null;

    if (this.decoder) {
      try {
        this.decoder.close();
      } catch {
        // Decoder may already be closed
      }
      this.decoder = null;
    }

    this._onFrame = null;
    this._onError = null;

    for (const texture of this.ringTextures) {
      texture.destroy();
    }
    this.ringTextures = [];
    this.ringIndex = 0;
    this.currentTexture = null;
    this.samples = [];
    this.displayToDts = [];
    this.decoderConfig = null;
    this._videoWidth = 0;
    this._videoHeight = 0;
    this.state = DecoderState.Idle;
  }
}
